import { useEffect, useRef, useState } from "react";
import { Flex, Select, SelectProps, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";

import { debounce } from "lodash";
import { PAGE_SIZE } from "@/utils/const";
import { SaleItemState } from "@/types/sales";
import { itemListWithOrg } from "@/apis/order";
import { useSelector } from "react-redux";
import { RootState } from "@/store";

interface SelectSaleOrderItemProps extends Omit<SelectProps, 'options' | 'fieldNames'> {
  onChangeItem?: (info: SaleItemState) => void;
  defaultList?: SaleItemState[];
  orgId?: string;
}

const SelectSaleOrderItem = ({
  onChangeItem,
  onChange,
  defaultList = [],
  orgId,
  ...props
}: SelectSaleOrderItemProps) => {
  const { t } = useTranslation();

  const [list, setList] = useState<Array<SaleItemState>>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(1);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>("");

  const abordRef = useRef<AbortController | null>(null);

  const curOrg = useSelector((state: RootState) => state.orgReducer.curOrg);
  const effectiveOrgId = orgId ?? curOrg.id;

  useEffect(() => {
    if (defaultList.length > 0) setList(last => [...last, ...defaultList]);
  }, [defaultList]);

  useEffect(() => () => abordRef.current?.abort(), []);

  const fetch = (keyword: string, current: number) => {
    setListLoading(true);
    abordRef.current?.abort();
    const controller = new AbortController();
    abordRef.current = controller;
    itemListWithOrg({
      body: { keyword },
      pagination: { offset: (current - 1) * PAGE_SIZE, take: PAGE_SIZE },
      controller,
    }, effectiveOrgId)
      .then((res) => {
        if (current === 1) {
          setList(res.body || []);
        } else {
          setList([...list, ...(res.body || [])]);
        }
        setTotal(res.pagination?.size ?? 0);
      })
      .catch(() => { })
      .finally(() => {
        setListLoading(false);
      });
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;
    if (
      currentPage * PAGE_SIZE < total &&
      target.scrollTop + target.offsetHeight >= target.scrollHeight &&
      !listLoading
    ) {
      let page = currentPage + 1;
      fetch(keyword, page);
      setCurrentPage(page);
    }
  };

  const onSearch = debounce((val: string) => {
    setKeyword(val.trim());
    setCurrentPage(1);
    fetch(val, 1);
  }, 500);

  const renderOption = (item: SaleItemState) => ({
    value: item.id,
    label: (
      <Flex gap={4} align="center">
        <Tag color="blue">{item.order?.number}</Tag>
        <span>{item.productName}</span>
      </Flex>
    ),
  });

  return (
    <Select
      loading={listLoading}
      filterOption={false}
      onSearch={onSearch}
      onDropdownVisibleChange={(open) => {
        if (open && list.length === defaultList.length) {
          setKeyword("");
          setCurrentPage(1);
          fetch("", 1);
        }
      }}
      onChange={(val, option) => {
        const item = list.find((i) => i.id === val);
        if (item) {
          onChangeItem?.(item);
        }
        onChange?.(val, option);
      }}
      style={{ width: "100%" }}
      showSearch
      placeholder={t("scan.selectPl")}
      labelInValue={false}
      options={list.map(renderOption)}
      onPopupScroll={handleScroll}
      dropdownRender={(ele) => (
        <>
          {ele}
          {listLoading && (
            <Flex
              style={{ width: "100%", padding: 10 }}
              justify="center"
              align="center"
            >
              <Spin />
            </Flex>
          )}
        </>
      )}
      {...props}
    />
  );
};

export default SelectSaleOrderItem;
